import { Code, Zap, Share, Brain } from 'lucide-react';

const BentoGrid = () => {
  return (
    <div className="py-24 sm:py-32">
      <div className="mx-auto max-w-2xl px-6 lg:max-w-7xl lg:px-8">
        <div className="text-center">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text text-transparent">
            Build What You Imagine
          </h2>
          <p className="mt-6 max-w-xl text-lg leading-8 text-muted-foreground mx-auto">
            Turn your ideas into reality with a framework that gets out of your way. Create, share, and iterate faster than ever before.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-4 sm:mt-16 lg:grid-cols-6 lg:grid-rows-[auto_auto]">
          {/* Main Feature Card */}
          <div className="relative lg:col-span-3">
            <div className="absolute inset-px rounded-lg bg-card max-lg:rounded-t-[2rem] lg:rounded-tl-[2rem]" />
            <div className="relative flex flex-col overflow-hidden rounded-[calc(theme(borderRadius.lg)+1px)] max-lg:rounded-t-[calc(2rem+1px)] lg:rounded-tl-[calc(2rem+1px)] group">
              <div className="h-36 bg-muted/50 flex items-center justify-center">
                <Code className="w-28 h-28 text-blue-500/50 group-hover:text-blue-500/80 transition-colors duration-200" />
              </div>
              <div className="px-10 pt-4 pb-4">
                <h3 className="text-sm/4 font-semibold text-primary">Zero Infrastructure</h3>
                <p className="mt-2 text-lg font-medium tracking-tight text-foreground">Browser-Based Data Apps</p>
                <p className="mt-2 max-w-lg text-sm/6 text-muted-foreground">
                  Export to fully static web apps that run entirely in the browser. No backend hosting, zero deployment friction, and data privacy by default.
                </p>
              </div>
            </div>
            <div className="pointer-events-none absolute inset-px rounded-lg shadow ring-1 ring-border/5 max-lg:rounded-t-[2rem] lg:rounded-tl-[2rem]" />
          </div>

          {/* AI Assistant Card */}
          <div className="relative lg:col-span-3">
            <div className="absolute inset-px rounded-lg bg-card lg:rounded-tr-[2rem]" />
            <div className="relative flex flex-col overflow-hidden rounded-[calc(theme(borderRadius.lg)+1px)] lg:rounded-tr-[calc(2rem+1px)] group">
              <div className="h-36 bg-muted/50 flex items-center justify-center">
                <Brain className="w-28 h-28 text-purple-500/50 group-hover:text-purple-500/80 transition-colors duration-200" />
              </div>
              <div className="px-10 pt-4 pb-4">
                <h3 className="text-sm/4 font-semibold text-primary">AI-Native</h3>
                <p className="mt-2 text-lg font-medium tracking-tight text-foreground">Built for AI Agents</p>
                <p className="mt-2 max-w-lg text-sm/6 text-muted-foreground">
                  Simple declarative UI primitives and reactive DAG engine make it easy for AI to understand, generate, and maintain apps.
                </p>
              </div>
            </div>
            <div className="pointer-events-none absolute inset-px rounded-lg shadow ring-1 ring-border/5 lg:rounded-tr-[2rem]" />
          </div>

          {/* Skip Setup Card */}
          <div className="relative lg:col-span-2">
            <div className="absolute inset-px rounded-lg bg-card lg:rounded-bl-[2rem]" />
            <div className="relative flex h-full flex-col overflow-hidden rounded-[calc(theme(borderRadius.lg)+1px)] lg:rounded-bl-[calc(2rem+1px)] group">
              <div className="h-48 bg-muted/50 flex items-center justify-center">
                <Zap className="w-32 h-32 text-yellow-500/50 group-hover:text-yellow-500/80 transition-colors duration-200" />
              </div>
              <div className="px-10 pt-4 pb-6">
                <h3 className="text-sm/4 font-semibold text-primary">Multi-Source</h3>
                <p className="mt-2 text-lg font-medium tracking-tight text-foreground">Lightweight Data Apps</p>
                <p className="mt-2 max-w-lg text-sm/6 text-muted-foreground">
                  Pull from CSV, JSON, Parquet, Clickhouse, Postgres, all configured declaratively. Runs in-browser or hybrid mode.
                </p>
              </div>
            </div>
            <div className="pointer-events-none absolute inset-px rounded-lg shadow ring-1 ring-border/5 lg:rounded-bl-[2rem]" />
          </div>

          {/* Interactivity Card */}
          <div className="relative lg:col-span-2">
            <div className="absolute inset-px rounded-lg bg-card" />
            <div className="relative flex h-full flex-col overflow-hidden rounded-[calc(theme(borderRadius.lg)+1px)] group">
              <div className="h-48 bg-muted/50 flex items-center justify-center">
                <Code className="w-32 h-32 text-green-500/50 group-hover:text-green-500/80 transition-colors duration-200" />
              </div>
              <div className="px-10 pt-4 pb-6">
                <h3 className="text-sm/4 font-semibold text-primary">For Developers</h3>
                <p className="mt-2 text-lg font-medium tracking-tight text-foreground">Faster, Simpler Framework</p>
                <p className="mt-2 max-w-lg text-sm/6 text-muted-foreground">
                  Open-source, runs in-browser, and supports WASM. A more reactive alternative to traditional data app frameworks.
                </p>
              </div>
            </div>
            <div className="pointer-events-none absolute inset-px rounded-lg shadow ring-1 ring-border/5" />
          </div>

          {/* Sharing Card */}
          <div className="relative lg:col-span-2">
            <div className="absolute inset-px rounded-lg bg-card max-lg:rounded-b-[2rem] lg:rounded-br-[2rem]" />
            <div className="relative flex h-full flex-col overflow-hidden rounded-[calc(theme(borderRadius.lg)+1px)] max-lg:rounded-b-[calc(2rem+1px)] lg:rounded-br-[calc(2rem+1px)] group">
              <div className="h-48 bg-muted/50 flex items-center justify-center">
                <Share className="w-32 h-32 text-red-500/50 group-hover:text-red-500/80 transition-colors duration-200" />
              </div>
              <div className="px-10 pt-4 pb-6">
                <h3 className="text-sm/4 font-semibold text-primary">For Business</h3>
                <p className="mt-2 text-lg font-medium tracking-tight text-foreground">Secure & Portable</p>
                <p className="mt-2 max-w-lg text-sm/6 text-muted-foreground">
                  Create and share interactive data tools that are secure, portable, and customizable. Perfect for consultants and analysts.
                </p>
              </div>
            </div>
            <div className="pointer-events-none absolute inset-px rounded-lg shadow ring-1 ring-border/5 max-lg:rounded-b-[2rem] lg:rounded-br-[2rem]" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BentoGrid; 